<template>
  <div class="brand-selection-page">
    <n-card class="brand-selection-card" title="Select Company">
      <p class="subtitle">
        Choose the company to work in for this session.
      </p>

      <n-spin :show="loading">
        <n-empty v-if="!loading && brands.length === 0" description="No companies available" />

        <div v-else class="brand-grid">
          <n-button
            v-for="brand in brands"
            :key="brand.id"
            class="brand-button"
            :type="brand.id === selectedBrandId ? 'primary' : 'default'"
            :ghost="brand.id !== selectedBrandId"
            @click="selectBrand(brand.id)"
          >
            <div class="brand-button-content">
              <strong>{{ brand.code }}</strong>
              <span>{{ brand.name }}</span>
            </div>
          </n-button>
        </div>
      </n-spin>

      <template #footer>
        <div class="footer-actions">
          <n-button @click="goToLogin" tertiary>
            Back to Login
          </n-button>
        </div>
      </template>
    </n-card>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useMessage } from 'naive-ui'
import { useRoute, useRouter } from 'vue-router'
import { apiClient } from '@/services/api'

interface Brand {
  id: string
  name: string
  code: string
}

const router = useRouter()
const route = useRoute()
const message = useMessage()

const loading = ref(false)
const brands = ref<Brand[]>([])
const selectedBrandId = ref<string | null>(localStorage.getItem('selectedBrandId'))

const loadBrands = async () => {
  loading.value = true
  try {
    const response = await apiClient.getBrands({ limit: 100 })
    brands.value = response.brands
  } catch (error) {
    message.error('Failed to load companies')
  } finally {
    loading.value = false
  }
}

const selectBrand = (brandId: string) => {
  selectedBrandId.value = brandId
  localStorage.setItem('selectedBrandId', brandId)

  const savedProjectId = localStorage.getItem(`selectedProject_${brandId}`)
  window.dispatchEvent(
    new CustomEvent('brand-project-changed', {
      detail: { brandId, projectId: savedProjectId },
    }),
  )

  const redirectPath = typeof route.query.redirect === 'string' ? route.query.redirect : '/'
  router.push(redirectPath)
}

const goToLogin = async () => {
  try {
    await apiClient.logout()
  } catch (error) {
    // ignore logout errors and still continue to login
  } finally {
    localStorage.removeItem('selectedBrandId')
    router.push('/login')
  }
}

onMounted(async () => {
  await loadBrands()

  const savedBrandId = selectedBrandId.value
  if (!savedBrandId) {
    return
  }

  const hasSavedBrand = brands.value.some((brand) => brand.id === savedBrandId)
  if (!hasSavedBrand) {
    localStorage.removeItem('selectedBrandId')
    selectedBrandId.value = null
  }
})
</script>

<style scoped>
.brand-selection-page {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--body-color);
  padding: 24px;
}

.brand-selection-card {
  width: min(720px, 100%);
}

.subtitle {
  margin-top: 0;
  margin-bottom: 16px;
  color: var(--text-color-2);
}

.brand-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.brand-button {
  height: 72px;
  justify-content: flex-start;
}

.brand-button-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.brand-button-content span {
  color: var(--text-color-2);
  font-size: 12px;
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
}
</style>
